import { useState, useEffect } from 'react'
import { getDb } from '../lib/db'
import { useInstantAuth } from './useInstantAuth'

export function useProducts(branchId = null, isOwnerOrManager = false) {
  const { business } = useInstantAuth()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {

    let active = true

    // If there's no authenticated business, bail out but avoid calling
    // setState synchronously from the effect body (wrap in microtask).
    if (!business?.id) {
      Promise.resolve().then(() => setLoading(false))
      return
    }

    // Avoid fetching business-wide products when no branch is selected and
    // the user isn't permitted to view all branches.
    if (!branchId && !isOwnerOrManager) {
      Promise.resolve().then(() => {
        setProducts([])
        setLoading(false)
      })
      return
    }
    let productSub = null
    let pricingSub = null

    getDb().then((db) => {
      if (!active) return

      // ── 1. Build merged product list from current base + pricing maps ─────
      function buildMerged(baseDocs, pricingDocs) {
        if (!branchId) {
          return baseDocs.map((d) => (d.toJSON ? d.toJSON() : d))
        }

        const pricingMap = {}
        pricingDocs.forEach((d) => {
          const j = d.toJSON ? d.toJSON() : d
          pricingMap[j.product_id] = j
        })

        return baseDocs.map((d) => {
          const p = d.toJSON ? d.toJSON() : d
          const pr = pricingMap[p.id]
          if (!pr) return p
          return {
            ...p,
            current_quantity: pr.current_quantity,
            buying_price: pr.buying_price,
            selling_price: pr.selling_price,
            reorder_point: pr.reorder_point,
            product_branch_pricing: pr,
          }
        })
      }

      // ── 2. Shared mutable state ───────────────────────────────────────────
      // Both subscriptions write here and call push() to recalculate.
      const shared = {
        baseDocs: [],
        pricingDocs: [],
        firstEmission: false,
      }

      function push() {
        if (!active) return
        const merged = buildMerged(shared.baseDocs, shared.pricingDocs)
        setProducts(merged)
        if (!shared.firstEmission) {
          shared.firstEmission = true
          setLoading(false) // ← unblock the UI on first local data
        }
      }

      // ── 3. Products subscription (reactive, fires immediately from IndexedDB)
      const productSelector = {
        business_id: business.id,
        _deleted: { $ne: true },
        $or: [{ is_active: true }, { is_active: null }],
      }

      productSub = db.products
        .find({ selector: productSelector, sort: [{ name: 'asc' }, { id: 'asc' }] })
        .$
        .subscribe({
          next: (docs) => {
            if (!active) return
            shared.baseDocs = docs
            push()
          },
          error: (err) => {
            console.error('[useProducts] products subscription error:', err)
            if (!active) return
            setLoading(false)
          },
        })

      // ── 4. Pricing subscription (only when branchId is set) ──────────────
      if (branchId && db.product_branch_pricing) {
        pricingSub = db.product_branch_pricing
          .find({
            selector: {
              branch_id: branchId,
              _deleted: { $ne: true },
            },
          })
          .$
          .subscribe({
            next: (docs) => {
              if (!active) return
              shared.pricingDocs = docs
              push()
            },
            error: (err) => {
              console.error('[useProducts] pricing subscription error:', err)
              // Don't block loading — just skip pricing merge
            },
          })
      }

      // Safety: if IndexedDB is completely empty and nothing fires within
      // 8 seconds, stop showing the spinner so the user sees "No products"
      // rather than an infinite loader.
      const fallbackTimer = setTimeout(() => {
        if (active && !shared.firstEmission) {
          shared.firstEmission = true
          setLoading(false)
        }
      }, 8000)

      // Store cleanup reference
      productSub._fallbackTimer = fallbackTimer
    }).catch((err) => {
      console.error('[useProducts] getDb error:', err)
      if (active) setLoading(false)
    })

    return () => {
      active = false
      clearTimeout(productSub?._fallbackTimer)
      productSub?.unsubscribe?.()
      pricingSub?.unsubscribe?.()
    }
  }, [business?.id, branchId, isOwnerOrManager])

  return { products, loading }
}


// ─────────────────────────────────────────────────────────────────────────────
// REPLICATION INIT — add this to your app boot sequence
// (e.g. in AuthContext, AppShell, or wherever you call startXReplication today)
//
// Replace the current pattern of calling startProductsReplication inside
// useProducts, and startOtherReplications wherever they currently live,
// with a single tiered boot call after the user is authenticated.
//
// import {
//   getDb,
//   startBranchesReplication,
//   startProductsReplication,
//   startProductBranchPricingReplication,
//   startSalesReplication,
//   startExpensesReplication,
//   startCustomersReplication,
//   startSaleItemsReplication,
//   startSalePaymentsReplication,
//   startStockEntriesReplication,
//   startInventoryMovementsReplication,
//   startStockTakesReplication,
//   startStockTakeItemsReplication,
//   startCreditAccountsReplication,
//   startCreditRepaymentsReplication,
//   startTransactionsReplication,
//   startStockTransfersReplication,
//   startBranchAssignmentsReplication,
//   startBranchFloatsReplication,
// } from '../lib/db'
//
// export async function bootReplication(businessId) {
//   const db = await getDb()
//
//   // Tier 1 — needed before ANY page can render (start immediately)
//   startBranchesReplication(db.branches, businessId)
//   startProductsReplication(db.products, businessId)
//   startProductBranchPricingReplication(db.product_branch_pricing, businessId)
//
//   // Tier 2 — needed for dashboard and transactions (start after 2s)
//   setTimeout(() => {
//     startSalesReplication(db.sales, businessId)
//     startExpensesReplication(db.expenses, businessId)
//     startCustomersReplication(db.customers, businessId)
//     startTransactionsReplication(db.transactions, businessId)
//   }, 2000)
//
//   // Tier 3 — background data (start after 8s)
//   setTimeout(() => {
//     startSaleItemsReplication(db.sale_items, businessId)
//     startSalePaymentsReplication(db.sale_payments, businessId)
//     startStockEntriesReplication(db.stock_entries, businessId)
//     startStockTransfersReplication(db.stock_transfers, businessId)
//     startBranchAssignmentsReplication(db.branch_assignments, businessId)
//     startBranchFloatsReplication(db.branch_floats, businessId)
//     startInventoryMovementsReplication(db.inventory_movements, businessId)
//     startStockTakesReplication(db.stock_takes, businessId)
//     startStockTakeItemsReplication(db.stock_take_items, businessId)
//     startCreditAccountsReplication(db.credit_accounts, businessId)
//     startCreditRepaymentsReplication(db.credit_repayments, businessId)
//   }, 8000)
// }
